import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRoundLeft as Undo2 } from "@semo/icons";
import {
  ButtonShadcn as Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  cn,
} from "@semo/shared-ui";

import {
  getWithdrawErrorMessage,
  useWithdrawQuoteResponse,
} from "../../application/hooks/useWithdrawQuoteResponse";
import {
  WITHDRAWAL_REASON_MAX_LENGTH,
  canWithdrawQuote,
  getMyQuoteWithdrawalState,
  type MyQuoteWithdrawalState,
  type WithdrawalSourceRequest,
} from "../../domain/quoteWithdrawal";

export const WITHDRAW_EXPLANATION =
  "철회하면 발주기관 화면에 ‘철회됨’으로 표시되고 다시 제출하거나 수정할 수 없습니다.";

/** «철회됨» 표시 — 철회한 견적에서 버튼 대신 보인다. */
export function WithdrawnQuoteBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600",
        className,
      )}
    >
      철회됨
    </span>
  );
}

export interface WithdrawQuoteButtonProps {
  state: MyQuoteWithdrawalState;
  /** 버튼은 숨기고 상태(«철회됨»)만 보인다 — 씨마켓 임베드처럼 상세 액션을 감추는 화면용 */
  readOnly?: boolean;
  className?: string;
}

/**
 * 견적 철회 (V-13 · 기능 리스트 «견적 제출·수정·철회»).
 *
 * - 철회한 견적: «철회됨» 표시만.
 * - 마감됐거나 계약이 선 견적: 아무것도 그리지 않는다(서버도 막는다).
 * - 그 밖: [견적 철회] → 확인 창(사유 선택, 500자) → 철회.
 */
export function WithdrawQuoteButton({
  state,
  readOnly = false,
  className,
}: WithdrawQuoteButtonProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const { mutate, isPending } = useWithdrawQuoteResponse();

  if (state.withdrawnAt) {
    return (
      <div className={cn("flex flex-wrap items-center gap-2", className)}>
        <WithdrawnQuoteBadge />
        <span className="text-xs text-slate-500">
          철회한 견적은 다시 제출하거나 수정할 수 없습니다.
        </span>
      </div>
    );
  }

  if (readOnly || !canWithdrawQuote(state)) return null;

  const handleOpenChange = (next: boolean) => {
    if (isPending) return;
    setOpen(next);
    if (!next) setReason("");
  };

  const handleConfirm = () => {
    mutate(
      { quoteResponseId: state.quoteResponseId, reason },
      {
        onSuccess: () => {
          toast.success("견적을 철회했습니다.");
          setOpen(false);
          setReason("");
        },
        onError: (error) => {
          toast.error(getWithdrawErrorMessage(error));
        },
      },
    );
  };

  return (
    <div className={className}>
      <Button
        type="button"
        variant="outline-destructive"
        size="sm"
        onClick={() => setOpen(true)}
      >
        <Undo2 className="mr-1 h-4 w-4" aria-hidden="true" />
        견적 철회
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="z-[210]">
          <DialogHeader>
            <DialogTitle>견적을 철회할까요?</DialogTitle>
            <DialogDescription>{WITHDRAW_EXPLANATION}</DialogDescription>
          </DialogHeader>

          <div className="space-y-1 py-2">
            <label
              htmlFor="withdraw-quote-reason"
              className="text-sm font-medium text-slate-700"
            >
              철회 사유 (선택)
            </label>
            <Textarea
              id="withdraw-quote-reason"
              placeholder="예: 납기를 맞출 수 없어 견적을 철회합니다."
              value={reason}
              maxLength={WITHDRAWAL_REASON_MAX_LENGTH}
              onChange={(e) =>
                setReason(e.target.value.slice(0, WITHDRAWAL_REASON_MAX_LENGTH))
              }
              className="min-h-[96px]"
              disabled={isPending}
            />
            <p className="text-right text-xs text-slate-400">
              {reason.length}/{WITHDRAWAL_REASON_MAX_LENGTH}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              취소
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={isPending}
            >
              {isPending ? "철회 중..." : "철회하기"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * 공고 상세 응답에서 내 견적을 찾아 철회 버튼(또는 «철회됨»)을 그린다.
 * 아직 견적을 내지 않은 공고면 아무것도 그리지 않는다.
 */
export function MyQuoteWithdrawal({
  quoteRequest,
  readOnly,
  className,
}: {
  quoteRequest: WithdrawalSourceRequest;
  readOnly?: boolean;
  className?: string;
}) {
  const state = getMyQuoteWithdrawalState(quoteRequest);
  if (!state) return null;
  return (
    <WithdrawQuoteButton
      state={state}
      readOnly={readOnly}
      className={className}
    />
  );
}
